import { IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  BulkUpdateEntitiesProgress,
  isProgressTerminal,
  useBulkUpdateEntitiesProgressQuery,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  getBulkUpdateBannerTitle,
  getBulkUpdateProgressDetails,
} from 'src/pages/CustomViews/components/bulk-update-banner.helpers';
import {
  getBulkUpdateViewContextKey,
  isBulkUpdateJobInPartnerScope,
  useBulkUpdateJobsStore,
  useGlobalStore,
} from 'src/store';
import { AppEvents, emitAppEvent } from 'src/utils/hooks/use-app-events';

export const BulkUpdateGlobalBanner = () => {
  const activeBulkUpdateJob = useBulkUpdateJobsStore.use.activeBulkUpdateJob();
  const sourceViewContextKey = useBulkUpdateJobsStore.use.bulkUpdateSourceViewContextKey();
  const dismissBulkUpdateJob = useBulkUpdateJobsStore.use.dismissBulkUpdateJob();
  const updateBulkUpdateJob = useBulkUpdateJobsStore.use.updateBulkUpdateJob();
  const markBulkUpdateViewForRefresh =
    useBulkUpdateJobsStore.use.markBulkUpdateViewForRefresh();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const contextKey = activeBulkUpdateJob
    ? getBulkUpdateViewContextKey(activeBulkUpdateJob.entityName, activeBulkUpdateJob.viewId)
    : null;

  const isJobInCurrentScope = isBulkUpdateJobInPartnerScope(
    activeBulkUpdateJob,
    selectedPartnerId,
  );

  const { data: bulkUpdateProgressData, loading: isBulkUpdateProgressLoading } =
    useBulkUpdateEntitiesProgressQuery(
      isJobInCurrentScope ? activeBulkUpdateJob?.jobId : null,
      2_000,
    );

  const bulkUpdateProgress = (
    bulkUpdateProgressData as
      | {
          bulkUpdateEntitiesProgress?: BulkUpdateEntitiesProgress;
        }
      | undefined
  )?.bulkUpdateEntitiesProgress;

  const normalizedStatus = bulkUpdateProgress?.status?.toLowerCase();
  const isTerminal = isProgressTerminal(normalizedStatus);
  const isSourceViewActive = Boolean(contextKey && sourceViewContextKey === contextKey);

  useEffect(() => {
    if (
      !activeBulkUpdateJob ||
      !bulkUpdateProgress?.status ||
      bulkUpdateProgress.status === activeBulkUpdateJob.status
    ) {
      return;
    }
    updateBulkUpdateJob(activeBulkUpdateJob.jobId, { status: bulkUpdateProgress.status });
  }, [activeBulkUpdateJob, bulkUpdateProgress?.status, updateBulkUpdateJob]);

  useEffect(() => {
    if (
      !activeBulkUpdateJob ||
      !activeBulkUpdateJob.isUndoInProgress ||
      !normalizedStatus ||
      (normalizedStatus !== 'undone' && normalizedStatus !== 'failed')
    ) {
      return;
    }
    updateBulkUpdateJob(activeBulkUpdateJob.jobId, { isUndoInProgress: false });
  }, [activeBulkUpdateJob, normalizedStatus, updateBulkUpdateJob]);

  useEffect(() => {
    if (
      !activeBulkUpdateJob ||
      !contextKey ||
      !isTerminal ||
      activeBulkUpdateJob.hasTerminalSyncApplied
    ) {
      return;
    }

    emitAppEvent(AppEvents.SearchRecordsInvalidated, {
      entityName: activeBulkUpdateJob.entityName,
      viewId: activeBulkUpdateJob.viewId,
    });
    markBulkUpdateViewForRefresh(contextKey);
    updateBulkUpdateJob(activeBulkUpdateJob.jobId, {
      hasTerminalSyncApplied: true,
    });
  }, [
    activeBulkUpdateJob,
    contextKey,
    isTerminal,
    markBulkUpdateViewForRefresh,
    updateBulkUpdateJob,
  ]);

  if (
    !activeBulkUpdateJob ||
    activeBulkUpdateJob.dismissed ||
    isSourceViewActive ||
    !isJobInCurrentScope ||
    (!isBulkUpdateProgressLoading && !bulkUpdateProgress)
  ) {
    return null;
  }

  const title = getBulkUpdateBannerTitle({
    operationKind: activeBulkUpdateJob.operationKind,
    status: normalizedStatus ?? undefined,
    isUndoInProgress: activeBulkUpdateJob.isUndoInProgress,
  });
  const details = getBulkUpdateProgressDetails({
    operationKind: activeBulkUpdateJob.operationKind,
    progress: bulkUpdateProgress,
    separator: ' - ',
  });
  const progressPercent =
    bulkUpdateProgress && bulkUpdateProgress.total > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round((bulkUpdateProgress.processed / bulkUpdateProgress.total) * 100),
          ),
        )
      : 0;

  return (
    <Stack
      sx={(theme) => ({
        position: 'fixed',
        left: '50%',
        bottom: '1.5rem',
        transform: 'translateX(-50%)',
        zIndex: 1100,
        borderRadius: '6.25rem',
        padding: '0.0625rem',
        background: `linear-gradient(135deg, ${theme.palette.custom.OV.sys.secondary}80, ${theme.palette.custom.OV.sys.outlineVariant}40)`,
        boxShadow: `0 0.5rem 2rem ${theme.palette.custom.OV.sys.scrim}33`,
        width: 'max-content',
        maxWidth: 'calc(100vw - 2rem)',
      })}
      direction="row"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={(theme) => ({
          minWidth: '17rem',
          maxWidth: 'min(56rem, calc(100vw - 2rem))',
          borderRadius: 'inherit',
          px: '1.25rem',
          py: '0.375rem',
          position: 'relative',
          overflow: 'hidden',
          backdropFilter: 'blur(1.25rem) saturate(180%)',
          WebkitBackdropFilter: 'blur(1.25rem) saturate(180%)',
          backgroundColor: `${theme.palette.custom.OV.sys.surfaceContainerHigh}B3`,
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              minWidth: 0,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {details}
          </Typography>
        </Stack>

        <IconButton
          size="small"
          onClick={() => dismissBulkUpdateJob(activeBulkUpdateJob.jobId)}
          aria-label="Dismiss bulk update status banner"
          sx={{ flex: '0 0 auto' }}
        >
          <DynamicIcon name="close" fontSize="small" />
        </IconButton>

        {!isTerminal || isBulkUpdateProgressLoading ? (
          <LinearProgress
            variant={bulkUpdateProgress?.total ? 'determinate' : 'indeterminate'}
            value={bulkUpdateProgress?.total ? progressPercent : undefined}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 2,
            }}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};
